// TEMPORARY MOCK ADAPTER for Medical Staff Management.
// In-memory only — state lives in this module's `staff` array and resets on hard reload.
// Mutations resolve with ~300 ms simulated latency. Errors mimic the backend envelope.
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter. See medical-staff.adapter.ts.

import { normalizePhone, PHONE_REGEX } from '@/shared/utils/phone';

import {
  MedicalStaffApiError,
  type AddOptionInput,
  type ExportMedicalStaffParams,
  type InviteMedicalStaffInput,
  type ListMedicalStaffParams,
  type ListMedicalStaffResult,
  type MedicalStaff,
  type MedicalStaffApi,
  type MedicalStaffOptions,
  type MedicalStaffSortKey,
  type SortDirection,
  type UpdateMedicalStaffInput,
} from '../types/medical-staff.types';

const LATENCY_MS = 300;
// Email format: TLD >=2 ASCII letters — mirror the real backend / FE form rule (aligns to the spec email rule).
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const NAME_MIN = 2;
const NAME_MAX = 50;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Default canonical option pool — KO seed (PRD §3.3). The prototype seeds these same
// 8 departments and 3 job roles; new options are added via addDepartmentOption /
// addJobRoleOption and persist for the mock session only.
const DEFAULT_DEPARTMENTS: readonly string[] = [
  '정신건강의학과',
  '신경과',
  '재활의학과',
  '내분비내과',
  '심장내과',
  '가정의학과',
  '간호부',
  '임상시험센터',
];
const DEFAULT_JOB_ROLES: readonly string[] = ['의사', '간호사', 'CRC'];

let departments: string[] = [...DEFAULT_DEPARTMENTS];
let jobRoles: string[] = [...DEFAULT_JOB_ROLES];

// Seed mirrors prototype `MockData.institutionManagers` (mock-data.js §34-50) — same
// 15 rows, normalized to the canonical 3-state status enum and ISO datetimes.
// Mapping: 가입완료 → ACTIVE, 미가입 → PENDING, 임시저장 → PENDING
// (collapsed per team decision), 탈퇴 → WITHDRAWN.
let staff: MedicalStaff[] = [
  {
    id: 50,
    name: '지의사',
    email: 'ji.doctor@uuh.ulsan.kr',
    phone: '010-5934-6017',
    institution: '울산대학교병원',
    department: '정신건강의학과',
    jobRole: '의사',
    registeredAt: '2023-12-22T00:00:00.000Z',
    status: 'ACTIVE',
  },
  {
    id: 49,
    name: '박간호',
    email: 'park.nurse@uuh.ulsan.kr',
    phone: '010-3821-7745',
    institution: '울산대학교병원',
    department: '간호부',
    jobRole: '간호사',
    registeredAt: '2023-12-15T00:00:00.000Z',
    status: 'ACTIVE',
    // PRD 03 §3.8 (v1.3): seed one pending self-withdraw request so the mock
    // exercises the list chip + Approve/Reject panel.
    withdrawRequest: { requestedAt: '2024-06-10T00:00:00.000Z' },
  },
  {
    id: 48,
    name: '이상담',
    email: 'lee.counselor@uuh.ulsan.kr',
    phone: '010-7712-3398',
    institution: '울산대학교병원',
    department: '임상시험센터',
    jobRole: 'CRC',
    registeredAt: '2024-01-08T00:00:00.000Z',
    status: 'ACTIVE',
  },
  {
    id: 47,
    name: '최복지',
    email: 'choi.welfare@uuh.ulsan.kr',
    phone: null,
    institution: '울산대학교병원',
    department: '재활의학과',
    jobRole: 'CRC',
    registeredAt: null,
    status: 'PENDING',
  },
  {
    id: 46,
    name: '정전문',
    email: 'jung.mhw@uuh.ulsan.kr',
    phone: '010-9923-5567',
    institution: '울산대학교병원',
    department: '신경과',
    jobRole: 'CRC',
    registeredAt: '2024-01-20T00:00:00.000Z',
    status: 'ACTIVE',
  },
  {
    id: 45,
    name: '한의사',
    email: 'han.doctor@uuh.ulsan.kr',
    phone: '010-6634-2219',
    institution: '울산대학교병원',
    department: '심장내과',
    jobRole: '의사',
    registeredAt: null,
    status: 'PENDING',
  },
  {
    id: 44,
    name: '윤간호',
    email: 'yoon.nurse@uuh.ulsan.kr',
    phone: '010-2278-9934',
    institution: '울산대학교병원',
    department: '가정의학과',
    jobRole: '간호사',
    registeredAt: '2024-02-10T00:00:00.000Z',
    status: 'ACTIVE',
  },
  {
    id: 43,
    name: '김현아',
    email: 'kim.ha@uuh.ulsan.kr',
    phone: '010-8891-4456',
    institution: '울산대학교병원',
    department: '내분비내과',
    jobRole: 'CRC',
    registeredAt: '2023-11-05T00:00:00.000Z',
    status: 'WITHDRAWN',
  },
  {
    id: 42,
    name: '송의사',
    email: 'song.doctor@uuh.ulsan.kr',
    phone: '010-3344-2211',
    institution: '울산대학교병원',
    department: '신경과',
    jobRole: '의사',
    registeredAt: '2024-03-01T00:00:00.000Z',
    status: 'ACTIVE',
  },
  {
    id: 41,
    name: '오간호',
    email: 'oh.nurse@uuh.ulsan.kr',
    phone: '010-5577-3344',
    institution: '울산대학교병원',
    department: '정신건강의학과',
    jobRole: '간호사',
    registeredAt: '2024-03-15T00:00:00.000Z',
    status: 'ACTIVE',
  },
  {
    id: 40,
    name: '류상담',
    email: 'ryu.counselor@uuh.ulsan.kr',
    phone: '010-7799-5566',
    institution: '울산대학교병원',
    department: '임상시험센터',
    jobRole: 'CRC',
    registeredAt: null,
    status: 'PENDING',
  },
  {
    id: 39,
    name: '강복지',
    email: 'kang.welfare@uuh.ulsan.kr',
    phone: '010-1133-7788',
    institution: '울산대학교병원',
    department: '내분비내과',
    jobRole: 'CRC',
    registeredAt: '2024-04-01T00:00:00.000Z',
    status: 'ACTIVE',
  },
  {
    id: 38,
    name: '임전문',
    email: 'lim.mhw@uuh.ulsan.kr',
    phone: '010-2244-8899',
    institution: '울산대학교병원',
    department: '재활의학과',
    jobRole: 'CRC',
    registeredAt: null,
    status: 'PENDING',
  },
  {
    id: 37,
    name: '배의사',
    email: 'bae.doctor@uuh.ulsan.kr',
    phone: '010-4466-1122',
    institution: '울산대학교병원',
    department: '가정의학과',
    jobRole: '의사',
    registeredAt: '2024-05-01T00:00:00.000Z',
    status: 'ACTIVE',
  },
  {
    id: 36,
    name: '서간호',
    email: 'seo.nurse@uuh.ulsan.kr',
    phone: '010-6688-3344',
    institution: '울산대학교병원',
    department: '간호부',
    jobRole: '간호사',
    registeredAt: '2024-05-15T00:00:00.000Z',
    status: 'ACTIVE',
  },
];

let nextId = Math.max(...staff.map((s) => s.id)) + 1;

function findById(id: number): MedicalStaff {
  const target = staff.find((x) => x.id === id);
  if (!target) {
    throw new MedicalStaffApiError('NOT_FOUND', '해당 의료진을 찾을 수 없습니다.');
  }
  return target;
}

function validateName(name: string): void {
  const trimmed = name.trim();
  if (trimmed.length < NAME_MIN || trimmed.length > NAME_MAX) {
    throw new MedicalStaffApiError('NAME_INVALID', '이름을 입력해주세요. (2~50자)', {
      fields: { name: '이름을 입력해주세요. (2~50자)' },
    });
  }
}

function validateEmail(email: string): void {
  if (!EMAIL_REGEX.test(email)) {
    throw new MedicalStaffApiError('EMAIL_INVALID', '올바른 이메일 형식을 입력해주세요.', {
      fields: { email: '올바른 이메일 형식을 입력해주세요.' },
    });
  }
}

function validatePhone(phone: string | null): void {
  const digits = normalizePhone(phone);
  if (digits && !PHONE_REGEX.test(digits)) {
    const message =
      '올바른 휴대폰 번호 형식을 입력해주세요. (010으로 시작하는 11자리 숫자)';
    throw new MedicalStaffApiError('PHONE_INVALID', message, {
      fields: { phone: message },
    });
  }
}

function validateDepartment(department: string): void {
  if (!department || !department.trim()) {
    throw new MedicalStaffApiError('DEPARTMENT_REQUIRED', '담당 과를 선택해주세요.', {
      fields: { department: '담당 과를 선택해주세요.' },
    });
  }
}

function validateJobRole(jobRole: string): void {
  if (!jobRole || !jobRole.trim()) {
    throw new MedicalStaffApiError('ROLE_REQUIRED', '직무를 선택해주세요.', {
      fields: { jobRole: '직무를 선택해주세요.' },
    });
  }
}

function ensureUniqueEmail(email: string): void {
  const taken = staff.some((s) => s.email.toLowerCase() === email.toLowerCase());
  if (taken) {
    throw new MedicalStaffApiError('EMAIL_DUPLICATE', '이미 등록된 이메일입니다.', {
      fields: { email: '이미 등록된 이메일입니다.' },
    });
  }
}

function validateOptionName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    throw new MedicalStaffApiError('OPTION_INVALID', '이름을 입력해주세요.', {
      fields: { name: '이름을 입력해주세요.' },
    });
  }
  return trimmed;
}

function ensureUniqueOption(list: readonly string[], value: string): void {
  const taken = list.some((v) => v.toLowerCase() === value.toLowerCase());
  if (taken) {
    throw new MedicalStaffApiError('OPTION_DUPLICATE', '이미 존재하는 항목입니다.', {
      fields: { name: '이미 존재하는 항목입니다.' },
    });
  }
}

function compareStaff(
  a: MedicalStaff,
  b: MedicalStaff,
  key: MedicalStaffSortKey,
  dir: SortDirection,
): number {
  const sign = dir === 'asc' ? 1 : -1;
  if (key === 'id') return (a.id - b.id) * sign;
  if (key === 'name') return a.name.localeCompare(b.name) * sign;
  if (key === 'email') return a.email.localeCompare(b.email) * sign;
  if (key === 'department') return a.department.localeCompare(b.department) * sign;
  if (key === 'jobRole') return a.jobRole.localeCompare(b.jobRole) * sign;
  // registeredAt: null always sinks regardless of direction (PRD §3.1 — '-' for Unregistered).
  const va = a.registeredAt;
  const vb = b.registeredAt;
  if (va === null && vb !== null) return 1;
  if (vb === null && va !== null) return -1;
  if (va === null && vb === null) return 0;
  return (va! < vb! ? -1 : va! > vb! ? 1 : 0) * sign;
}

function filterAndSort(params: ExportMedicalStaffParams): MedicalStaff[] {
  const { q, department, status } = params;
  const sortBy: MedicalStaffSortKey = params.sortBy ?? 'id';
  const sortDir: SortDirection = params.sortDir ?? 'desc';

  let filtered = [...staff];
  if (department) filtered = filtered.filter((s) => s.department === department);
  if (status) filtered = filtered.filter((s) => s.status === status);
  if (q) {
    const needle = q.trim().toLowerCase();
    if (needle) {
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(needle) ||
          s.email.toLowerCase().includes(needle) ||
          s.department.toLowerCase().includes(needle),
      );
    }
  }
  filtered.sort((a, b) => compareStaff(a, b, sortBy, sortDir));
  return filtered;
}

export const medicalStaffMockApi: MedicalStaffApi = {
  async listMedicalStaff(params: ListMedicalStaffParams): Promise<ListMedicalStaffResult> {
    await delay();

    const page = Math.max(1, params.page ?? 1);
    const perPage = Math.max(1, params.perPage ?? 20);

    const filtered = filterAndSort(params);
    const total = filtered.length;
    const start = (page - 1) * perPage;
    const items = filtered.slice(start, start + perPage);

    return clone({ items, total, page, perPage });
  },

  async exportMedicalStaff(params: ExportMedicalStaffParams): Promise<MedicalStaff[]> {
    await delay();
    return clone(filterAndSort(params));
  },

  async inviteMedicalStaff(input: InviteMedicalStaffInput): Promise<MedicalStaff> {
    await delay();
    validateName(input.name);
    validateEmail(input.email);
    validatePhone(input.phone);
    validateDepartment(input.department);
    validateJobRole(input.jobRole);
    ensureUniqueEmail(input.email);

    const created: MedicalStaff = {
      id: nextId++,
      name: input.name.trim(),
      email: input.email.trim(),
      phone: input.phone,
      institution: '울산대학교병원',
      department: input.department,
      jobRole: input.jobRole,
      registeredAt: null,
      status: 'PENDING',
    };
    staff = [created, ...staff];
    return clone(created);
  },

  async resendInvitation(id: number): Promise<void> {
    await delay();
    const target = findById(id);
    if (target.status !== 'PENDING') {
      throw new MedicalStaffApiError(
        'INVALID_TARGET_STATUS',
        '미가입 상태의 의료진에게만 재발송할 수 있습니다.',
      );
    }
  },

  async updateMedicalStaff(id: number, input: UpdateMedicalStaffInput): Promise<MedicalStaff> {
    await delay();
    const target = findById(id);

    if (input.name !== undefined) {
      validateName(input.name);
      target.name = input.name.trim();
    }
    if (input.phone !== undefined) {
      validatePhone(input.phone);
      target.phone = input.phone;
    }
    if (input.department !== undefined) {
      validateDepartment(input.department);
      target.department = input.department;
    }
    if (input.jobRole !== undefined) {
      validateJobRole(input.jobRole);
      target.jobRole = input.jobRole;
    }
    if (input.status !== undefined) {
      target.status = input.status;
    }
    return clone(target);
  },

  async withdrawMedicalStaff(id: number): Promise<MedicalStaff> {
    await delay();
    const target = findById(id);
    if (target.status === 'WITHDRAWN') {
      throw new MedicalStaffApiError(
        'INVALID_TARGET_STATUS',
        '이미 탈퇴 처리된 의료진입니다.',
      );
    }
    target.status = 'WITHDRAWN';
    target.withdrawRequest = null;
    return clone(target);
  },

  // PRD 03 §3.8 (v1.3): approve the pending self-withdraw request → WITHDRAWN, clear request.
  async approveWithdrawMedicalStaff(id: number): Promise<MedicalStaff> {
    await delay();
    const target = findById(id);
    if (!target.withdrawRequest) {
      throw new MedicalStaffApiError(
        'INVALID_TARGET_STATUS',
        '탈퇴 요청이 없는 의료진입니다.',
      );
    }
    target.status = 'WITHDRAWN';
    target.withdrawRequest = null;
    return clone(target);
  },

  // PRD 03 §3.8 (v1.3): reject the pending self-withdraw request → clear request, status unchanged.
  async rejectWithdrawMedicalStaff(id: number): Promise<MedicalStaff> {
    await delay();
    const target = findById(id);
    if (!target.withdrawRequest) {
      throw new MedicalStaffApiError(
        'INVALID_TARGET_STATUS',
        '탈퇴 요청이 없는 의료진입니다.',
      );
    }
    target.withdrawRequest = null;
    return clone(target);
  },

  async resetPassword(id: number): Promise<void> {
    await delay();
    const target = findById(id);
    // PRD 03 §3.7: only Registered staff can receive a password-reset email
    // (Unregistered → no password yet; Withdrawn → login permanently blocked).
    if (target.status !== 'ACTIVE') {
      throw new MedicalStaffApiError(
        'INVALID_TARGET_STATUS',
        '가입완료 상태의 의료진에게만 비밀번호를 초기화할 수 있습니다.',
      );
    }
  },

  async listOptions(): Promise<MedicalStaffOptions> {
    await delay(100);
    return clone({ departments, jobRoles });
  },

  async addDepartmentOption(input: AddOptionInput): Promise<MedicalStaffOptions> {
    await delay();
    const name = validateOptionName(input.name);
    ensureUniqueOption(departments, name);
    departments = [...departments, name];
    return clone({ departments, jobRoles });
  },

  async addJobRoleOption(input: AddOptionInput): Promise<MedicalStaffOptions> {
    await delay();
    const name = validateOptionName(input.name);
    ensureUniqueOption(jobRoles, name);
    jobRoles = [...jobRoles, name];
    return clone({ departments, jobRoles });
  },
};
